import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress, Stack } from '@mui/material';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import { REVERSE_PROXY_IP } from 'src/global/config/common.config';
import { defaultProjectInput } from 'src/global/constants/project.constant';
import { ProjectStageEnum, RewardType } from 'src/global/enums/project.enum';
import { ProjectDetailType } from 'src/global/schemas/project.schema';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDomainVerification } from 'src/hooks/useDomainVerification';
import { useSaveDraft } from 'src/hooks/useSaveDraft';
import { useStagingPublish } from 'src/hooks/useStagingPublish';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { useTheme } from 'src/provider/ThemeCustomizationProvider';
import { useGetAdminWalletQuery } from 'src/store/services/project.service';
import { convertAwsImageUrlToFile } from 'src/utils/helpers';
import { getProjectValidationSchema } from 'src/utils/project';

import { FundingDistribution } from './FundingDistribution/FundingDistribution';
import GeneralProjectInfo from './GeneralInfo/GeneralInfo';
import { NodeSaleActionButtons } from './NodeSaleActionButtons';
import NodeSaleSettings from './NodeSaleSettings/NodeSaleSettings';
import { NodeSystemIntegration } from './NodeSystemIntegration';
import { PricingModel } from './PricingModel/PricingModel';
import ReferralProgram from './ReferralProgram/ReferralProgram';
import { RoyaltyFee } from './RoyaltyFee/RoyaltyFee';
import { ThemeCustomization } from './ThemeCustomization/ThemeCustomization';
import { TokenConfiguration } from './TokenConfiguration/TokenConfiguration';
import { VerifyDNSModal, VerifyDNSModalHandle } from './VerifyDNSModal';

interface EditNodeSaleFormProps {
  defaultValues: ProjectDetailType | undefined;
  isDetail?: boolean;
}

export const EditNodeSaleForm = ({ defaultValues, isDetail = false }: EditNodeSaleFormProps) => {
  const [isFormLoading, setIsFormLoading] = useState(true);
  const verifyDnsModalRef = useRef<VerifyDNSModalHandle>(null);
  const draftRef = useRef<any>(null);
  const { address } = useAccount();
  const { theme, setSelectedThemeMode, updateTheme, executeThemeValidation } = useTheme();
  const { setHasDeployed } = useDeployment();

  const validationSchema = getProjectValidationSchema(defaultValues?.state);

  const methods = useForm<ProjectInputs>({
    resolver: zodResolver(validationSchema),
    defaultValues: defaultProjectInput,
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const { data: adminWallet } = useGetAdminWalletQuery({ address: address as string }, { skip: !address });
  const { saveDraftAsync, isSaving } = useSaveDraft(methods.trigger);
  const { publishStagingAsync, isPublishing } = useStagingPublish();
  const nagivate = useNavigate();

  const goHome = useCallback(() => nagivate('/core-sale'), [nagivate]);
  const {
    isDomainDnsVerified,
    isDomainVerified,
    isLoading: isVerifyingDomain,
    verifyDomainData,
    verifyAndCreateDomain,
    verifyDomain,
  } = useDomainVerification(methods, address as string | undefined);

  useEffect(() => {
    if (!defaultValues || !adminWallet) return;
    (async () => {
      setIsFormLoading(true);
      // convert logo, background, favicon urls to file objects
      const imageUrls: Record<string, Promise<File> | null> = {
        background: defaultValues?.theme?.theme?.background?.image
          ? convertAwsImageUrlToFile(defaultValues?.theme?.theme?.background?.image as unknown as string)
          : null,
        logo: defaultValues?.theme?.image?.logo ? convertAwsImageUrlToFile(defaultValues?.theme?.image?.logo) : null,
        favicon: defaultValues?.theme?.image?.favicon
          ? convertAwsImageUrlToFile(defaultValues?.theme?.image?.favicon)
          : null,
        featureImage: defaultValues?.theme?.image?.featureImage
          ? convertAwsImageUrlToFile(defaultValues?.theme?.image?.featureImage)
          : null,
        nodeAsset: defaultValues?.node?.asset ? convertAwsImageUrlToFile(defaultValues?.node?.asset) : null,
      };

      const filterUrls = Object.fromEntries(Object.entries(imageUrls).filter(([_, value]) => value !== null));
      const results = await Promise.all(
        Object.entries(filterUrls).map(([key, task]) => (task as Promise<File>).then(data => ({ key, data }))),
      );
      const convertResults = results.reduce(
        (acc, { key, data }) => {
          acc[key] = data;
          return acc;
        },
        {} as Record<string, any>,
      );
      // update form state
      setHasDeployed(
        [ProjectStageEnum.PRODUCTION, ProjectStageEnum.STAGING, ProjectStageEnum.PRODUCTION].includes(
          defaultValues.state as ProjectStageEnum,
        ),
      );
      setSelectedThemeMode(defaultValues?.theme?.modeTheme);
      updateTheme({
        ...defaultValues.theme,
        theme: {
          ...defaultValues.theme.theme,
          background: {
            ...defaultValues.theme.theme.background,
            image: convertResults.background,
          },
        },
        image: {
          ...defaultValues.theme.image,
          logo: convertResults.logo,
          favicon: convertResults.favicon,
          featureImage: convertResults.featureImage,
        },
      });
      methods.reset({
        ...defaultValues,
        info: {
          ...defaultValues.info,
          dns: {
            type: 'CNAME',
            value: REVERSE_PROXY_IP,
            host: defaultValues?.info?.nodeSaleUrl || '',
          },
          isCustomDomain: !!defaultValues?.info?.nodeSaleUrl,
        },
        setting: {
          ...defaultValues.setting,
          defaultToken: defaultValues?.setting?.defaultToken,
        } as any,
        node: {
          ...defaultValues.node,
          asset: [convertResults.nodeAsset],
        },
        referral: {
          ...defaultValues.referral,
          rewardLv:
            defaultValues.referral.rewardType === RewardType.ERC_20 || !defaultValues?.referral?.rewardLv?.length
              ? [{ node: 0 }, { node: 0 }]
              : defaultValues?.referral?.rewardLv.map((reward: any) => ({ node: reward })),
        },
        recipients: !defaultValues?.recipients?.length
          ? [{ name: 'LFG', address: adminWallet.addr }]
          : defaultValues?.recipients,
        kols:
          !defaultValues?.kols?.length &&
          defaultValues.referral.isEnableDiscountKol &&
          defaultValues.referral.isEnableDiscount
            ? [{ address: '', discount: 0 }]
            : defaultValues?.kols,
        royalties: !defaultValues?.royalties?.length ? [{ address: '', percent: 0 }] : defaultValues?.royalties,
        phases: !defaultValues?.phases?.length
          ? [
              {
                totalNode: 0,
                recipients: [
                  {
                    address: adminWallet.addr,
                    percent: adminWallet.minPercent,
                  },
                ],
                isEdit: true,
              },
            ]
          : defaultValues?.phases.map((phase: any) => ({
              ...phase,
              recipients: phase.recipients.map((recipient: any) => ({
                ...recipient,
                percent: recipient.percent,
              })),
              referalPercent: defaultValues?.referral?.percentage,
              rewardType: defaultValues?.referral?.rewardType,
            })),
      });
      setIsFormLoading(false);
    })();
  }, [defaultValues, adminWallet]);

  const handleSaveDraft = useCallback(async () => {
    try {
      const isValid = await methods.trigger('name');
      if (!isValid || !address || !defaultValues || !theme) return;

      const formValues = methods.getValues();
      await saveDraftAsync(defaultValues, formValues as unknown as ProjectInputs, theme);
      toast.success('Save successfully');
      goHome();
    } catch (error: any) {
      toast.error(error?.message ?? 'Save failed');
    }
  }, [methods, address, defaultValues, theme, saveDraftAsync, goHome]);

  const publishProject = useCallback(async () => {
    try {
      if (!draftRef.current || !defaultValues) throw new Error('Draft is missing');
      await publishStagingAsync(defaultValues, methods.getValues(), draftRef.current);
      toast.success('Publish successfully');
      goHome();
    } catch (error) {
      toast.error('Publish project failed');
    }
  }, [defaultValues, methods, publishStagingAsync, goHome]);

  const onSubmit = useCallback(async () => {
    try {
      const invalid = executeThemeValidation?.();
      if (invalid || !defaultValues || !theme) return;

      const formValues = methods.getValues();

      if (!draftRef.current) {
        draftRef.current = await saveDraftAsync(defaultValues, formValues as ProjectInputs, theme, true);
        if (!draftRef.current) throw new Error('Save draft failed');
      }

      const verified = await verifyAndCreateDomain(() => verifyDnsModalRef?.current?.show());
      if (!verified) return;

      await publishProject();
    } catch {
      toast.error('Publish project failed');
    }
  }, [defaultValues, theme, methods, executeThemeValidation, saveDraftAsync, verifyAndCreateDomain, publishProject]);

  const handleVerifyDomain = useCallback(async () => {
    try {
      const verified = await verifyDomain();
      if (!verified) {
        toast.info('DNS may take time to propagate. Please try again later.');
        return;
      }

      verifyDnsModalRef?.current?.hide();
      await publishProject();
    } catch {
      toast.error('Publish project failed');
    }
  }, [verifyDomain, publishProject]);

  const handleDeployProject = useCallback(async () => {
    executeThemeValidation?.();
    methods.handleSubmit(onSubmit)();
  }, [executeThemeValidation, methods, onSubmit]);

  const renderContent = () => {
    if (isFormLoading) {
      return (
        <Stack alignItems={'center'} justifyContent={'center'} minHeight={'100vh'} width={'100%'}>
          <CircularProgress />
        </Stack>
      );
    }

    return (
      <Fragment>
        <GeneralProjectInfo isDisabled={isDetail} />
        <NodeSaleSettings isDetail={isDetail} />
        <TokenConfiguration isDisabled={isDetail} />
        <PricingModel isDetail={isDetail} />
        <ReferralProgram
          defaultIsEnable={!!defaultValues?.referral.isEnable}
          defaultIsDonationEnable={!!defaultValues?.referral?.isEnableDonation}
          isDetail={isDetail}
        />
        <FundingDistribution adminWallet={adminWallet} isDetail={isDetail} />
        <RoyaltyFee isDetail={isDetail} />
        <ThemeCustomization />
        <NodeSystemIntegration isDisabled={isDetail} />
      </Fragment>
    );
  };

  const renderCTAButtons = () => {
    return (
      <NodeSaleActionButtons
        status={defaultValues?.state as ProjectStageEnum}
        isFormLoading={isFormLoading}
        isSaving={isSaving}
        isPublishing={isPublishing}
        deployProject={handleDeployProject}
        handleSaveDraft={handleSaveDraft}
      />
    );
  };

  return (
    <FormProvider {...methods}>
      <form className='flex flex-col gap-y-4' onSubmit={methods.handleSubmit(onSubmit)}>
        {renderContent()}
        {renderCTAButtons()}
        <VerifyDNSModal
          ref={verifyDnsModalRef}
          onSubmit={handleVerifyDomain}
          loading={isVerifyingDomain}
          verifyDomainData={verifyDomainData}
          isVerified={isDomainVerified}
          isDnsVerified={isDomainDnsVerified}
        />
      </form>
    </FormProvider>
  );
};
